/* Mats · Buscador del header (sections/mats-search.liquid)
   - Abre con la lupa del header, cualquier [data-mats-search-open] o la tecla «/».
   - Búsqueda predictiva: /search/suggest?q=…&section_id=mats-search-results
   - Esc o «Cerrar» cierra; Enter va a la página de resultados. */
(() => {
  if (customElements.get('mats-search')) return;

  class MatsSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-sr-dialog]');
      this.form = this.querySelector('[data-sr-form]');
      this.input = this.querySelector('[data-sr-input]');
      this.clearBtn = this.querySelector('[data-sr-clear]');
      this.start = this.querySelector('[data-sr-start]');
      this.results = this.querySelector('[data-sr-results]');
      this.progress = this.querySelector('[data-sr-progress]');
      this.cache = new Map();
      this.timer = null;
      this.ctrl = null;

      this._onDocClick = (e) => {
        // La lupa del header de Prestige (a[aria-controls="header-search-…"]) también abre este panel
        const opener = e.target.closest('[data-mats-search-open], .header__search-link a, a[aria-controls^="header-search"]');
        if (!opener) return;
        e.preventDefault();
        e.stopPropagation();
        this.open(opener);
      };
      this._onKey = (e) => {
        if (e.key !== '/' || this.dialog.open) return;
        const t = e.target;
        if (t.closest('input, textarea, select, [contenteditable="true"]')) return;
        e.preventDefault();
        this.open();
      };
      // Al añadir al carrito (quick view / tarjeta) se cierra para que se vea el carrito lateral
      this._onCartAdd = () => this.close();
      document.addEventListener('click', this._onDocClick, true);
      document.addEventListener('keydown', this._onKey);
      document.addEventListener('mats:cart:add', this._onCartAdd);

      this.querySelector('[data-sr-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
      // En un input type=search, Esc solo borra el texto: aquí cierra siempre
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || document.querySelector('mats-quick-view dialog[open]')) return;
        e.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });

      this.input.addEventListener('input', () => this.onInput());
      this.clearBtn.addEventListener('click', () => { this.input.value = ''; this.onInput(); this.input.focus(); });
      this.form.addEventListener('submit', (e) => { if (!this.input.value.trim()) e.preventDefault(); });

      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-sr-term]');
        if (chip) {
          this.input.value = chip.dataset.srTerm;
          this.onInput(true);
          this.input.focus();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('keydown', this._onKey);
      document.removeEventListener('mats:cart:add', this._onCartAdd);
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener || document.activeElement;
      document.documentElement.classList.add('mb-sr-lock');
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      requestAnimationFrame(() => {
        this.input.focus();
        this.input.select();
      });
      if (this.input.value.trim()) this.onInput(true);
    }

    close() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.dialog.classList.add('is-closing');
      const done = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.documentElement.classList.remove('mb-sr-lock');
        this.opener?.focus?.({ preventScroll: true });
      };
      reduce ? done() : setTimeout(done, 260);
    }

    onInput(now = false) {
      const q = this.input.value.trim();
      this.clearBtn.hidden = !q;
      clearTimeout(this.timer);
      if (!q) {
        this.ctrl?.abort();
        this.setLoading(false);
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.start.hidden = false;
        return;
      }
      this.timer = setTimeout(() => this.fetch(q), now ? 0 : 220);
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
    }

    async fetch(q) {
      const key = q.toLowerCase();
      if (this.cache.has(key)) return this.render(this.cache.get(key), q);

      this.ctrl?.abort();
      this.ctrl = new AbortController();
      this.setLoading(true);

      const limit = this.dataset.limit || 8;
      const params = new URLSearchParams({
        q,
        section_id: 'mats-search-results',
        'resources[type]': 'product,collection,query',
        'resources[limit]': limit,
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,tag,vendor'
      });

      try {
        const res = await fetch(`${this.dataset.resultsUrl}?${params}`, { signal: this.ctrl.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        this.cache.set(key, html);
        if (this.input.value.trim().toLowerCase() === key) this.render(html, q);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.setLoading(false);
      }
    }

    render(html, q) {
      this.setLoading(false);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const res = doc.querySelector('[data-sr-res]');
      if (!res) return;

      const count = res.dataset.count || '0';
      res.querySelectorAll('[data-sr-t]').forEach((el) => {
        const k = el.dataset.srT;
        let txt = this.dataset[`t${k.replace(/(^|-)(\w)/g, (_, __, c) => c.toUpperCase())}`] || '';
        txt = txt.replace('[count]', count).replace('[terms]', q);
        el.textContent = txt;
      });

      this.results.replaceChildren(res);
      this.results.hidden = false;
      this.start.hidden = true;

      // Estado de guardados en las tarjetas nuevas
      if (window.MatsWishlist) {
        res.querySelectorAll('[data-mats-wish]').forEach((b) =>
          b.setAttribute('aria-pressed', String(window.MatsWishlist.has(b.dataset.matsWish)))
        );
      }
    }
  }

  customElements.define('mats-search', MatsSearch);
})();
